import React, { FC, useEffect, useState } from 'react'
import styled, { keyframes } from 'styled-components'
import { useScreenClass } from 'react-grid-system'
import { Link, useLocation } from 'react-router-dom'

// Announcement for the Descript Scholarship deadline extension. The desktop
// bar reappears on every page and every visit by design. The mobile version is
// a full-screen modal, so it is limited to a first-time visitor arriving on the
// home page — Layout remounts on every route change, which would otherwise
// re-open it on each navigation.
const POST_PATH = '/blog/6bllP2H6dsa6u93ly0jtjq'
const DEADLINE = new Date(2026, 8, 20) // Sept 20 2026, local time
const DEADLINE_LABEL = 'September 20'
// Bump the suffix if the announcement changes and past visitors should see it
// again.
const SEEN_KEY = 'descript-scholarship-modal-seen-v1'

const hasSeenModal = (): boolean => {
  try {
    return window.localStorage.getItem(SEEN_KEY) === '1'
  } catch {
    // Private mode / storage blocked: fall back to showing it this visit.
    return false
  }
}

const markModalSeen = (): void => {
  try {
    window.localStorage.setItem(SEEN_KEY, '1')
  } catch {
    // Nothing to do — the modal simply shows again next visit.
  }
}

const daysLeft = (): number => {
  const now = new Date()
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  return Math.max(
    0,
    Math.round((DEADLINE.getTime() - today.getTime()) / 86400000)
  )
}

const pulse = keyframes`
  0%   { box-shadow: 0 0 0 0 rgba(241, 234, 134, 0.7); }
  70%  { box-shadow: 0 0 0 8px rgba(241, 234, 134, 0); }
  100% { box-shadow: 0 0 0 0 rgba(241, 234, 134, 0); }
`

const shimmer = keyframes`
  0%   { transform: translateX(-100%); }
  100% { transform: translateX(100%); }
`

const Bar = styled.div`
  position: relative;
  background: linear-gradient(62deg, #002244 0%, #00549A 100%);
  color: white;
  padding: 0.7rem 3.5rem 0.7rem 1.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  overflow: hidden;
  font-size: 0.98rem;
  line-height: 1.35;

  /* slow light sweep so the bar reads as live rather than static chrome */
  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(
      100deg,
      transparent 0%,
      rgba(255, 255, 255, 0.09) 50%,
      transparent 100%
    );
    animation: ${shimmer} 6s ease-in-out infinite;
    pointer-events: none;
  }

  @media (max-width: 1100px) {
    padding: 0.7rem 3rem 0.7rem 1rem;
    font-size: 0.9rem;
    gap: 0.75rem;
  }
`

const Dot = styled.span`
  width: 9px;
  height: 9px;
  border-radius: 50%;
  background: #F1EA86;
  flex-shrink: 0;
  animation: ${pulse} 2s infinite;
`

const Chip = styled.span`
  background: rgba(241, 234, 134, 0.16);
  border: 1px solid rgba(241, 234, 134, 0.5);
  color: #F1EA86;
  font-weight: 700;
  font-size: 0.8rem;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  padding: 0.2rem 0.6rem;
  border-radius: 999px;
  white-space: nowrap;
  flex-shrink: 0;
`

const Message = styled.span`
  strong {
    font-weight: 700;
  }
`

const BarCTA = styled(Link)`
  background: ${(props) => props.theme.colors.red};
  color: white;
  text-decoration: none;
  font-weight: 700;
  font-size: 0.85rem;
  letter-spacing: 0.03em;
  padding: 0.45rem 1.1rem;
  border-radius: 999px;
  white-space: nowrap;
  flex-shrink: 0;
  transition: background-color 0.25s ease, transform 0.25s ease;

  &:hover {
    background: #6B000D;
    transform: translateX(3px);
  }
  &:focus-visible {
    outline: 2px solid white;
    outline-offset: 2px;
  }
`

const Dismiss = styled.button`
  position: absolute;
  right: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: rgba(255, 255, 255, 0.7);
  font-size: 1.4rem;
  line-height: 1;
  cursor: pointer;
  padding: 0 0.4rem;

  &:hover {
    color: white;
  }
  &:focus-visible {
    outline: 2px solid white;
    outline-offset: 2px;
  }
`

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.6);
  /* Above the floating Calendly badge, which injects itself at 9998. */
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1.5rem;
`

const Modal = styled.div`
  background: white;
  border-radius: 16px;
  max-width: 420px;
  width: 100%;
  padding: 2rem 1.5rem 1.5rem;
  position: relative;
  text-align: center;
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.35);
  border-top: 6px solid ${(props) => props.theme.colors.red};

  .eyebrow {
    text-transform: uppercase;
    letter-spacing: 2px;
    font-size: 0.75rem;
    color: ${(props) => props.theme.colors.red};
    font-weight: 700;
    margin: 0 0 0.75rem;
  }

  h2 {
    font-size: 2rem;
    margin: 0 0 1rem;
    color: #222;
    line-height: 1.3;
  }

  p {
    color: ${(props) => props.theme.colors.darkGray};
    line-height: 1.6;
    margin: 0 0 1.5rem;
  }
`

// Doubled specificity: this is a <p> inside Modal, whose `p { ... }` rule would
// otherwise win on specificity and paint the countdown dark gray on navy.
const ModalChip = styled.p`
  && {
    display: inline-block;
    background: #002244;
    color: #F1EA86;
    font-weight: 700;
    font-size: 0.85rem;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    padding: 0.35rem 1rem;
    border-radius: 999px;
    margin: 0 0 1rem;
  }
`

const ModalClose = styled.button`
  position: absolute;
  top: 0.5rem;
  right: 0.75rem;
  background: none;
  border: none;
  font-size: 1.75rem;
  line-height: 1;
  color: #4B4B4B;
  cursor: pointer;
  padding: 0.25rem 0.5rem;

  &:focus-visible {
    outline: 2px solid ${(props) => props.theme.colors.red};
    outline-offset: 2px;
  }
`

const CTA = styled(Link)`
  display: block;
  background-color: ${(props) => props.theme.colors.red};
  color: white;
  text-decoration: none;
  font-weight: 600;
  letter-spacing: 1px;
  padding: 0.9rem 1.5rem;
  border-radius: 8px;
  transition: background-color 0.3s;

  &:hover {
    background-color: #6B000D;
  }
`

const Later = styled.button`
  background: none;
  border: none;
  color: #4B4B4B;
  margin-top: 0.75rem;
  cursor: pointer;
  font-size: 0.9rem;
  text-decoration: underline;
`

const DescriptScholarship: FC = () => {
  const screenClass = useScreenClass()
  const isMobile = ['xs', 'sm'].includes(screenClass)
  const isHome = useLocation().pathname === '/'
  const [open, setOpen] = useState(true)
  // Read once on mount so dismissing does not immediately re-render the modal
  // away mid-interaction; the flag only takes effect on the next visit.
  const [seen] = useState(hasSeenModal)
  const showModal = isMobile && isHome && !seen
  const days = daysLeft()
  const countdown =
    days === 0 ? 'Last day' : days === 1 ? '1 day left' : `${days} days left`

  useEffect(() => {
    if (!showModal || !open) return
    markModalSeen()
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('keydown', onKey)
    const previous = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', onKey)
      document.body.style.overflow = previous
    }
  }, [showModal, open])

  if (!open) return null

  // Mobile keeps the modal-or-nothing behaviour: no announcement bar is shown
  // at these widths.
  if (isMobile) {
    return showModal ? (
      <Overlay onClick={() => setOpen(false)}>
        <Modal
          role="dialog"
          aria-modal="true"
          aria-labelledby="descript-modal-title"
          onClick={(e) => e.stopPropagation()}
        >
          <ModalClose aria-label="Close" onClick={() => setOpen(false)}>
            ×
          </ModalClose>
          <p className="eyebrow">Deadline Extended</p>
          <ModalChip>{countdown}</ModalChip>
          <h2 id="descript-modal-title">
            The Descript Scholarship is open through {DEADLINE_LABEL}
          </h2>
          <p>
            Descript is funding one participant’s full tuition for the
            Incubator, and every participant gets a free year of Descript.
          </p>
          <CTA to={POST_PATH} onClick={() => setOpen(false)}>
            Read the announcement
          </CTA>
          <Later onClick={() => setOpen(false)}>Maybe later</Later>
        </Modal>
      </Overlay>
    ) : null
  }

  return (
    <Bar>
      <Dot aria-hidden="true" />
      <Chip>{countdown}</Chip>
      <Message>
        <strong>Deadline extended to {DEADLINE_LABEL}</strong> — Descript is
        funding one participant’s full tuition.
      </Message>
      <BarCTA to={POST_PATH}>Read the announcement →</BarCTA>
      <Dismiss aria-label="Dismiss announcement" onClick={() => setOpen(false)}>
        ×
      </Dismiss>
    </Bar>
  )
}

export default DescriptScholarship
